import initSqlJs, {Database} from 'sql.js';
import {cachedIdbConnection} from '../../indexeddb/cached-idb-connection';

export abstract class AbstractSqliteDatabase {

    private db: Database;
    private readonly storeName : string;
    private readonly dbFileKey : string;

    protected constructor(storeName: string, dbFileKey: string) {
        this.storeName = storeName;
        this.dbFileKey = dbFileKey;
    }

    async saveAndLoadDb(dbFile: Uint8Array) : Promise<void>{
        await saveToIndexedDb(this.storeName, this.dbFileKey , dbFile);
        await this.loadDb();
    }

    async loadDb() : Promise<void>{
        const dbFile = await loadFromIndexedDb(this.storeName, this.dbFileKey);
        if(!dbFile){
            throw new Error('Database not loaded')
        }
        const SQL = await initSqlJs({ locateFile: file => {return `/assets/sql-wasm.wasm` } });
        this.db = new SQL.Database(dbFile);
    }

    async exists() : Promise<boolean>{
        const dbFile = await loadFromIndexedDb(this.storeName, this.dbFileKey);
        return !!dbFile;
    }

    async count(sql: string): Promise<number> {
        if (!this.db) {
            await this.loadDb();
        }
        const result = this.db.exec(sql);
        if (!result.length) return 0;
        const row = result[0].values[0];
        if (!row || !row.length) return 0;
        return Number(row[0]);
    }

    async countWithParams(sql: string, sqlParams): Promise<number> {
        if (!this.db) {
            await this.loadDb();
        }
        const stmt = this.db.prepare(sql);
        try {
            stmt.bind(sqlParams);
            if (stmt.step()) {
                const row = stmt.getAsObject();
                const val = Object.values(row)[0];

                return typeof val === "number" ? val : 0;
            }
            return 0;
        } finally {
            stmt.free();
        }
    }

    async queryObjects<T = any>(sql: string, sqlParams: Record<string, any>,  jsonColumns: string[] = []): Promise<T[]> {
        if (!this.db) {
            await this.loadDb();
        }

        let result = [];

        if(sqlParams) {
            result = this.db.exec(sql, sqlParams);
        } else {
            result = this.db.exec(sql);
        }

        if (!result || result.length === 0) return [];

        const { columns, values } = result[0];

        const jsonColsSet = new Set(jsonColumns.map(c => c.toLowerCase()));

        return values.map(row => {
            const obj: any = {};

            columns.forEach((col, i) => {
                let val = row[i];

                const shouldForceJson = jsonColsSet.has(col.toLowerCase());

                if (shouldForceJson) {
                    if (val === null || val === undefined) {
                        obj[col] = val;
                    } else if (typeof val === 'string') {
                        try {
                            obj[col] = JSON.parse(val);
                        } catch (e) {
                            // fallback : raw string
                            obj[col] = val;
                        }
                    } else {
                        obj[col] = val;
                    }
                } else {
                    obj[col] = tryParseJson(val);
                }
            });

            return obj as T;
        });
    }
}

function tryParseJson(value: any) {
    if (value === null || value === undefined) return value;
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
        return value;
    }
    try {
        return JSON.parse(trimmed);
    } catch (e) {
        return value;
    }
}

const DB_NAME = 'sqlite_cache';
const getDb = cachedIdbConnection(DB_NAME, ['cmc_product_manager_store', 'standard_product_manager_store', 'parameters_store']);

export async function saveToIndexedDb(storeName: string, key: string, value: any) {
    const db = await getDb();
    await db.put(storeName, value, key);
}

export async function loadFromIndexedDb(storeName: string, key: string): Promise<any> {
    const db = await getDb();
    return db.get(storeName, key);
}
